import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import axios from 'axios';
import '../styles/StoryViewer.css';

const StoryViewer = ({ stories, initialStoryIndex = 0, onClose }) => {
  const { user } = useSelector(state => state.auth);
  const [currentIndex, setCurrentIndex] = useState(initialStoryIndex);
  const [progress, setProgress] = useState(0);
  const [reactions, setReactions] = useState([]);
  const [showReactionPicker, setShowReactionPicker] = useState(false);
  const [duration, setDuration] = useState(10000); // 10 seconds for images
  const [showMenu, setShowMenu] = useState(false);
  const videoRef = useRef(null);
  const token = localStorage.getItem('token');

  const currentStory = stories[currentIndex];
  const isVideo = currentStory?.image?.includes('.mp4') || currentStory?.image?.includes('video');
  const isAuthor = user?._id === currentStory?.author?._id;

  // Get video duration
  useEffect(() => {
    if (isVideo && videoRef.current) {
      const video = videoRef.current;
      const handleLoadedMetadata = () => {
        const videoDuration = Math.round(video.duration * 1000); // convert to ms
        setDuration(videoDuration || 10000);
      };
      video.addEventListener('loadedmetadata', handleLoadedMetadata);
      return () => {
        video.removeEventListener('loadedmetadata', handleLoadedMetadata);
      };
    } else {
      setDuration(10000); // 10 seconds for images
    }
  }, [isVideo, currentIndex]);

  // Progress bar timer - adjusted based on duration
  useEffect(() => {
    let interval;
    
    // Always playing stories with proper timing
    if (duration > 0 && stories.length > 0) {
      const startTime = Date.now();
      let lastProgress = 0;
      
      interval = setInterval(() => {
        const elapsed = Date.now() - startTime;
        const newProgress = (elapsed / duration) * 100;
        
        // Update only if progress has changed significantly
        if (Math.abs(newProgress - lastProgress) > 0.5 || newProgress >= 100) {
          lastProgress = newProgress;
          
          if (newProgress >= 100) {
            clearInterval(interval);
            // Move to next story
            if (currentIndex < stories.length - 1) {
              setCurrentIndex(currentIndex + 1);
              setProgress(0);
              setReactions([]);
            } else {
              onClose();
            }
          } else {
            setProgress(newProgress);
          }
        }
      }, 16); // ~60fps
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [currentIndex, duration, stories.length, onClose]);

  useEffect(() => {
    if (currentStory) {
      setReactions(currentStory.reactions || []);
      // Mark as viewed
      axios.post(
        `${process.env.REACT_APP_API_URL}/stories/view/${currentStory._id}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      ).catch(err => console.error('Error viewing story:', err));
    }
  }, [currentStory, token]);

  const handleNext = () => {
    if (currentIndex < stories.length - 1) {
      setCurrentIndex(currentIndex + 1);
      setProgress(0);
      setReactions([]);
    } else {
      onClose();
    }
  };

  const handlePrev = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
      setProgress(0);
      setReactions([]);
    }
  };

  const handleReaction = async (emoji) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/stories/${currentStory._id}/react`,
        { emoji },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setReactions(response.data.story.reactions);
      setShowReactionPicker(false);
    } catch (err) {
      console.error('Error adding reaction:', err);
    }
  };

  const handleRemoveReaction = async () => {
    try {
      const response = await axios.delete(
        `${process.env.REACT_APP_API_URL}/stories/${currentStory._id}/react`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setReactions(response.data.story.reactions);
    } catch (err) {
      console.error('Error removing reaction:', err);
    }
  };

  const handleDeleteStory = async () => {
    if (window.confirm('Delete this story?')) {
      try {
        await axios.delete(
          `${process.env.REACT_APP_API_URL}/stories/${currentStory._id}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        window.location.reload();
      } catch (err) {
        console.error('Error deleting story:', err);
        alert('Error deleting story');
      }
    }
  };

  const userReaction = reactions.find(r => r.user._id === user?._id || r.user === user?._id);
  const emojiOptions = ['❤️', '😂', '😮', '😢', '🔥', '👍'];
  const formatTime = (ms) => {
    const seconds = Math.floor(ms / 1000);
    return `${seconds}s`;
  };

  return (
    <div className="story-viewer-modal">
      <div className="story-viewer-container">
        {/* Progress bars */}
        <div className="story-progress-bars">
          {stories.map((_, index) => (
            <div
              key={index}
              className="progress-bar"
              style={{
                width: `${100 / stories.length}%`,
              }}
            >
              <div
                className="progress-fill"
                style={{
                  width: index === currentIndex ? `${progress}%` : index < currentIndex ? '100%' : '0%',
                }}
              />
            </div>
          ))}
        </div>

        {/* Story content */}
        <div className="story-viewer-content">
          {isVideo ? (
            <video
              ref={videoRef}
              src={`http://localhost:5000${currentStory.image}`}
              autoPlay
              className="story-media"
              onEnded={handleNext}
            />
          ) : (
            <img
              src={`http://localhost:5000${currentStory.image}`}
              alt="Story"
              className="story-media"
            />
          )}

          {/* Text overlay */}
          {currentStory?.text && (
            <div className="story-text-overlay">
              <p>{currentStory.text}</p>
            </div>
          )}

          {/* Duration indicator */}
          <div className="story-duration">
            ⏱️ {formatTime(duration)}
          </div>

          {/* Author info with menu */}
          <div className="story-author-info">
            <div className="author-avatar">
              {currentStory?.author?.profilePicture ? (
                <img
                  src={`http://localhost:5000${currentStory.author.profilePicture}`}
                  alt={currentStory.author.username}
                />
              ) : (
                <span>
                  {currentStory?.author?.firstName?.charAt(0)}
                  {currentStory?.author?.lastName?.charAt(0)}
                </span>
              )}
            </div>
            <div className="author-details">
              <div className="author-name">
                {currentStory?.author?.firstName} {currentStory?.author?.lastName}
              </div>
              <div className="author-username">@{currentStory?.author?.username}</div>
            </div>

            {isAuthor && (
              <div className="story-menu-container">
                <button className="story-menu-btn" onClick={() => setShowMenu(!showMenu)}>
                  ⋮
                </button>
                {showMenu && (
                  <div className="story-dropdown-menu">
                    <button onClick={handleDeleteStory} className="delete-option">
                      🗑️ Delete
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Reactions */}
          <div className="story-reactions-container">
            <div className="reactions-list">
              {reactions.map((reaction, index) => (
                <div key={index} className="reaction-badge" title={reaction.user.username}>
                  {reaction.emoji}
                </div>
              ))}
            </div>

            <button
              className={`reaction-btn ${userReaction ? 'has-reaction' : ''}`}
              onMouseEnter={() => setShowReactionPicker(true)}
              onClick={() => handleRemoveReaction()}
            >
              {userReaction ? userReaction.emoji : '😊'}
            </button>

            {showReactionPicker && (
              <div className="reaction-picker" onMouseLeave={() => setShowReactionPicker(false)}>
                {emojiOptions.map(emoji => (
                  <button
                    key={emoji}
                    className="emoji-option"
                    onClick={() => handleReaction(emoji)}
                  >
                    {emoji}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* View details */}
          <div className="story-view-info">
            👁️ {currentStory?.views?.length || 0} views
          </div>
        </div>

        {/* Navigation */}
        {currentIndex > 0 && (
          <button className="story-nav-btn story-prev" onClick={handlePrev}>
            ‹
          </button>
        )}

        {currentIndex < stories.length - 1 && (
          <button className="story-nav-btn story-next" onClick={handleNext}>
            ›
          </button>
        )}

        {/* Close button */}
        <button className="story-close-btn" onClick={onClose}>
          ✕
        </button>

        {/* Footer info */}
        <div className="story-footer">
          <div className="story-counter">
            {currentIndex + 1} / {stories.length}
          </div>
        </div>
      </div>
    </div>
  );
};

export default StoryViewer;
